import React, { PureComponent } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { store } from 'redux-rest-reducer';
import { v4 as uuidv4 } from 'uuid';

import { post as postVibes } from 'actions/vibes';
import { dateObjToEpoch, capitalizeAll } from 'utils';
import { handleAWSS3Upload, getAWSSignedKey } from 'modals/utils';
import VibeComponent from './view';

const TYPE = ['student', 'teacher'];
const userVibeQuery = { type: 'user_vibe' };
const KEYS = {
  USER_TYPE: 'user_type',
  VIBE_NAME: 'vibe_name',
  START_DATE: 'start_date',
  END_DATE: 'end_date',
  CODE: 'code',
  USERS_FILE_NAME: 'users_file_name',
};
let timeout = null;

class CreateVibe extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      vibeObj: {},
      value: null,
      dateErrorMsg: null,
      dateSuccessMsg: null,
      vibePost: store.post(),
      showConfirmation: false,
      message: null,
      errorText: null,
      submitDone: false,
      open: false,
      staleState: true,
      isDisabled: false,
    };
  }

  static getDerivedStateFromProps(props, state) {
    const { vibes } = props;
    const { vibePost, staleState } = state;

    const postVibeDetails = store.post(vibes, vibePost.id);
    const { resolved } = postVibeDetails || {};

    const newState = {};
    if (resolved && staleState) {
      const { isCreated, isCreatingFailed } = resolved;
      if (isCreated) {
        newState.submitDone = true;
        newState.errorText = '';
        newState.message = 'Vibe successfully added';
      }
      if (isCreatingFailed) {
        newState.submitDone = true;
        newState.message = '';
        newState.errorText = 'Error encountered! Please verify the inputs provided.';
      }
      newState.staleState = false;
    }

    return newState;
  }

  componentDidUpdate(prevProps, prevState) {
    const { submitDone, message } = this.state;
    if (!prevState.submitDone && submitDone) {
      timeout = setTimeout(() => {
        this.handleCancel();
        if (message) {
          this.resetFields();
        }
      }, 1000);
    }
  }

  componentWillUnmount = () => {
    clearTimeout(timeout);
  };

  resetFields = () => {
    this.setState({
      vibeObj: {
        vibe_name: '',
        code: '',
        start_date: '',
        end_date: '',
        user_type: '',
      },
      value: null,
      dateErrorMsg: null,
      dateSuccessMsg: null,
      isDisabled: false,
    });
  };

  createPostObj = (fieldInput, key) => {
    const { vibeObj } = this.state;
    const newObj = Object.assign({}, vibeObj);
    if (key === KEYS.USER_TYPE) {
      newObj[key] = fieldInput.value;
      this.setState({ vibeObj: newObj, value: fieldInput });
    } else {
      newObj[key] = fieldInput;
      this.setState({ vibeObj: newObj });
    }
  };

  successMessage = (startingDate, endingDate) =>
    `Vibe will be live from ${new Date(startingDate * 1000).toLocaleDateString('en-US', {
      day: 'numeric',
    })} ${new Date(startingDate * 1000).toLocaleDateString('en-US', {
      month: 'short',
    })}. ${new Date(startingDate * 1000).toLocaleDateString('en-US', {
      year: 'numeric',
    })} 12 AM to ${new Date(endingDate * 1000).toLocaleDateString('en-US', {
      day: 'numeric',
    })} ${new Date(endingDate * 1000).toLocaleDateString('en-US', {
      month: 'short',
    })}. ${new Date(endingDate * 1000).toLocaleDateString('en-US', {
      year: 'numeric',
    })} 11:59 PM in Indian Standard Time(IST)`;

  dateChecker = (startingDate, endingDate) => {
    if (startingDate <= endingDate) {
      this.setState({
        dateSuccessMsg: this.successMessage(startingDate, endingDate),
        dateErrorMsg: null,
      });
    } else {
      this.setState({
        dateErrorMsg: 'Start date cannot be greater than end date',
        dateSuccessMsg: null,
      });
    }
  };

  handleStartDate = e => {
    const { vibeObj } = this.state;
    const newObj = Object.assign({}, vibeObj);
    newObj.start_date = dateObjToEpoch(e.target.value);
    const { start_date: startDate, end_date: endDate } = newObj || {};

    if (endDate) {
      let startingDate = startDate;
      let endingDate = endDate;
      this.dateChecker(startingDate, endingDate);
    }
    this.setState({ vibeObj: newObj });
  };

  handleEndDate = e => {
    const { vibeObj } = this.state;
    const newObj = Object.assign({}, vibeObj);
    newObj.end_date = dateObjToEpoch(e.target.value);
    const { start_date: startDate, end_date: endDate } = newObj || {};

    if (startDate) {
      const startingDate = startDate;
      const endingDate = endDate;
      this.dateChecker(startingDate, endingDate);
    }
    this.setState({ vibeObj: newObj });
  };

  setValue = val => {
    const types = TYPE.map(type => ({ label: type, value: type }));
    const selection = val && types.filter(item => item.value === val.value);
    return selection;
  };

  disableCreateVibe = () => {
    const { vibeObj } = this.state;
    const {
      vibe_name: vibeName,
      start_date: startDate,
      end_date: endDate,
      user_type: userType,
      users_file_name: usersFileName,
      code,
    } = vibeObj || {};
    let d1 = new Date(startDate);
    let d2 = new Date(endDate);

    return !(
      vibeName &&
      startDate &&
      endDate &&
      userType &&
      code &&
      usersFileName &&
      d1 <= d2
    );
  };

  handleCSVUploadToS3 = (files, randomId, resolve, reject) => {
    const { awsSignedKey } = this.state;

    Array.from(files).forEach(file => {
      const key = `media/vibes/${randomId}.csv`;

      handleAWSS3Upload(file, key, awsSignedKey, resolve, reject);
    });
  };

  handleDataUpload = async (files, randomId) => {
    return new Promise((resolve, reject) => {
      this.handleCSVUploadToS3(files, randomId, resolve, reject);
    });
  };

  onConfirm = async () => {
    const {
      vibeObj,
      vibePost: { id: postId },
    } = this.state;
    const { users_file_name: usersFileName, user_type: userType } = vibeObj || {};

    const { actions } = this.props;
    const randomId = `${uuidv4()}`;
    const csvFileName = `${randomId}_csv`;

    const postObj = {
      ...vibeObj,
      user_type: capitalizeAll(userType),
      users_file_name: csvFileName,
    };

    this.setState({ staleState: true, isDisabled: true });
    await this.handleDataUpload(usersFileName, randomId);
    actions.postVibes(postId, postObj);
  };

  handleCreate = async () => {
    const { awsSignedKey } = this.state;
    if (!awsSignedKey || awsSignedKey.isStale) {
      const awsSignedKey = await getAWSSignedKey(userVibeQuery);
      this.setState({
        awsSignedKey,
      });
    }
    this.setState({ showConfirmation: true, open: true });
  };

  handleCancel = () => {
    this.setState({
      showConfirmation: false,
      open: false,
      errorText: null,
      message: null,
      submitDone: false,
      isDisabled: false,
    });
  };

  render() {
    const {
      vibeObj,
      value,
      dateErrorMsg,
      dateSuccessMsg,
      showConfirmation,
      errorText,
      message,
      open,
      isDisabled,
    } = this.state;

    return (
      <VibeComponent
        isDisabled={isDisabled}
        vibeObj={vibeObj}
        value={value}
        dateErrorMsg={dateErrorMsg}
        dateSuccessMsg={dateSuccessMsg}
        showConfirmation={showConfirmation}
        errorText={errorText}
        message={message}
        open={open}
        handleCancel={this.handleCancel}
        handleCreate={this.handleCreate}
        onConfirm={this.onConfirm}
        disableCreateVibe={this.disableCreateVibe}
        setValue={this.setValue}
        handleEndDate={this.handleEndDate}
        handleStartDate={this.handleStartDate}
        createPostObj={this.createPostObj}
      />
    );
  }
}

export default connect(
  ({ vibes }) => ({
    vibes,
  }),
  dispatch => ({
    actions: bindActionCreators({ postVibes }, dispatch),
  }),
)(CreateVibe);
